<script lang="ts">
import { useAxios } from '@/shared/composition/useAxios';
import { configEnv } from '@/shared/config-env';
import { showCardOffBanner } from '@/shared/lib/core/cards';
import { eventsGenerate } from '@/shared/lib/events/eventsGenerate';
import { eventsHandle } from '@/shared/lib/events/eventsHandle';
import { bitrixKeepSyncPartnerId } from '@/shared/lib/domains/bitrix/bitrixKeepSyncPartnerId';
import { bitrixKeepSyncBitrixId } from '@/shared/lib/domains/bitrix/bitrixKeepSyncBitrixId';
import { validClientUrls } from '@/shared/plugins/find_client_by_url';
import { useBitrixStore } from '@/shared/store/useBitrixStore';
import { useEventsStore } from '@/shared/store/useEventsStore';
import { useOrdersStore } from '@/shared/store/useOrdersStore';
import { useRatingStore } from '@/shared/store/useRatingStore';
import { useUserStore } from '@/shared/store/useUserStore';
import {
  defineComponent,
  nextTick,
} from 'vue';
import { handleRuntimeError } from '@/shared/lib/core/errors';
import {
  bitrixEmailChange,
  differentEmailFromBitrix,
  matchedOldEmailFromBitrix,
} from '@/shared/store/bitrixEmailChange';
import { svcService } from '@/app/modules/svc/svcService';
import {
  isRequestBodyFilled,
  requestModel,
  REQUEST_TARGETS,
} from '@/app/request/RequestModel';
import { request } from '@/app/request/request';
import { RequestFactory } from '@/shared/module/RequestFactory';
import axios from 'axios';
import { releaseAfterAppReady } from '@/callAfterAppReady';
import { useSentryAsync } from '@/shared/composition/useSentryAsync';
import { useViewAsOwner } from '@/shared/composition';

const { axiosGlobal } = useAxios();
const { captureExceptionSentry } = useSentryAsync();

export default defineComponent({
  name: 'App',
  provide: {
    synergyRuRequest: new RequestFactory(axios.create({
      baseURL: configEnv.SYNERGY_URL,
    })),
  },
  data: () => ({
    error: false,
    isInitialized: false,
  }),
  computed: {
    // eslint-disable-next-line vue/no-unused-properties
    isCardDisabled() {
      const userStore = useUserStore();
      return showCardOffBanner(userStore.getUserInfo.physicalUserCard.status);
    },
    needToFindOldEmailOnServiceByBitrixId() {
      return bitrixEmailChange.needToFindOldEmailOnServiceByBitrixId.value;
    },
  },
  watch: {
    async needToFindOldEmailOnServiceByBitrixId() {
      const bitrixStore = useBitrixStore();
      const id = bitrixStore.bitrixId;
      const oldEmails = bitrixStore.oldEmails as string[];
      try {
        if (!id) {
          // Если id нет - запрос не делаем
          return;
        }
        const response = await svcService.emailByBitrixId(String(id));

        const indexOfMatchedEmail = oldEmails.indexOf(response.response.email);
        if (response.response.email && indexOfMatchedEmail !== -1) {
          bitrixEmailChange.isOldEmailFoundOnServiceByBitrixId.value = true;
          matchedOldEmailFromBitrix.value = oldEmails[indexOfMatchedEmail];
        } else if (response.response.email) {
          differentEmailFromBitrix.value = response.response.email;
          bitrixEmailChange.isFoundDifferentEmailOnServiceByBitrixId.value = true;
        }
      } catch (error: any) {
        if (error?.response?.status === 404) {
          return;
        }
        throw error;
      } finally {
        bitrixEmailChange.isRequestOldEmailOnServiceByBitrixIdDone.value = true;
      }
    },
  },
  async mounted() {
    releaseAfterAppReady();
    const userStore = useUserStore();
    const bitrixStore = useBitrixStore();
    const eventsStore = useEventsStore();
    const ordersStore = useOrdersStore();
    const ratingStore = useRatingStore();
    await bitrixStore.fetchClientInformation().catch(err => handleRuntimeError(err));
    eventsStore.fetchEvents();
    document.title = this.$t('general.pageTitle').toString();
    const redirect = await this.checkIfRedirectNeeded();
    const { ownerEmail } = useViewAsOwner();
    if (redirect && !ownerEmail.value) {
      window.location.replace(redirect);
      return;
    }
    (window as any).appStore = {
      userStore,
      bitrixStore,
      eventsStore,
      ordersStore,
      ratingStore,
    };
    try {
      await Promise.all([
        userStore.fetchUserInfo(),
        userStore.fetchRefRegistrations(),
        userStore.fetchRefPayments(),
        ordersStore.fetchOrders(),
      ]);

      if (bitrixStore.client.id) {
        let updateRequest = requestModel({
          method: 'POST',
          url: '/api/user/update',
          target: REQUEST_TARGETS.BITRIX,
        });
        updateRequest = bitrixKeepSyncPartnerId(updateRequest);
        updateRequest = bitrixKeepSyncBitrixId(updateRequest);

        if (isRequestBodyFilled(updateRequest)) {
          await request(updateRequest);
        }
      }

      this.isInitialized = true;

      nextTick(() => {
        this.afterCommonsFetched();
      });
    } catch (error) {
      if (configEnv.VUE_APP_ENVIRONMENT === 'development') {
        console.error(error);
      }
      captureExceptionSentry(error, 'монтирование апп сф');
      this.isInitialized = true;
      this.error = true;
    }
    eventsGenerate();
    eventsHandle();
  },
  methods: {
    afterCommonsFetched() {
      // Переопределяем, чтобы сделать что-то после получения основных данных
    },

    async checkIfRedirectNeeded() {
      if (configEnv.DISABLE_REDIRECT) {
        return false;
      }

      const program = await this.getLoyaltyProgram();

      if (configEnv.VUE_APP_ENVIRONMENT !== 'development') {
        const { origin } = window.location;
        if (origin !== program) {
          return program;
        }
      }
    },

    async getLoyaltyProgram() {
      try {
        const res = await axiosGlobal.head('/v2/api');
        return res.headers.location;
      } catch (error) {
        captureExceptionSentry(error, 'получение пл сф');
        return validClientUrls.SYNERGY_FRIENDS;
      }
    },
  },
});
</script>
